import { useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Toolbar, IconButton, Menu, MenuItem, Button, Box, Divider, List, useMediaQuery } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
// import AddCommentIcon from '@mui/icons-material/AddComment';
// import FactCheckIcon from '@mui/icons-material/FactCheck';
import MenuIcon from '@mui/icons-material/Menu';
import MuiAppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import LogoutIcon from "@mui/icons-material/Logout";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { globalStyle } from "../../global/Utils";
import StartIcon from '@mui/icons-material/Start';


const drawerWidth = 200;
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        [theme.breakpoints.up("md")]: {
          marginLeft: drawerWidth,
          width: `calc(100% - ${drawerWidth}px)`,
        },
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": {
        ...openedMixin(theme),
        backgroundColor: globalStyle.bgColorWhite,
      },
    }
    : {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": {
        ...closedMixin(theme),
        backgroundColor: globalStyle.bgColorWhite,
      },
    }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AdminHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerOpen = () => {
    if (!isMobile) {
      setOpen(true);
    }
  };

  const handleDrawerClose = () => {
    if (!isMobile) {
      setOpen(false);
    }
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace("/login");
  };

  // const handleNavigation = (path) => {
  //   navigate(path);
  //   if (isMobile) {
  //     setMobileOpen(false);
  //   }
  // };

  const drawerContent = (
    <>
      <DrawerHeader></DrawerHeader>
      <Divider />
      <List>
        <ListItem disablePadding sx={{ backgroundColor: "#e6eaef" }}>
          <ListItemButton
            sx={{ justifyContent: open ? "initial" : "center" }}
            onClick={() => navigate("/main/add-project")}
          >
            <ListItemIcon sx={{ justifyContent: "center" }}>
              <StartIcon
                sx={{
                  color: location.pathname === "/main/add-project" ? globalStyle.blueColor : "",
                  transition: "color 0.3s ease",
                }}
              />
            </ListItemIcon>
            <Tooltip title="Add Project">
              <ListItemText
                primary="Add Project"
                sx={{
                  opacity: open ? 1 : 0,
                  color: location.pathname === "/main/add-project" ? globalStyle.blueColor : "",
                }}
                primaryTypographyProps={{
                  fontSize: 14, // Change font size
                  fontFamily: globalStyle.fontFamilyParagraph, // Change font family
                  fontWeight: "bold", // Optional: Change font weight
                }}
              />
            </Tooltip>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ backgroundColor: "#e6eaef" }}>
          <ListItemButton
            sx={{ justifyContent: open ? "initial" : "center" }}
            onClick={() => navigate("/main/view-project")}
          >
            <ListItemIcon sx={{ justifyContent: "center" }}>
              <StartIcon
                sx={{
                  color: location.pathname === "/main/view-project" ? globalStyle.blueColor : "",
                  transition: "color 0.3s ease",
                }}
              />
            </ListItemIcon>
            <Tooltip title="Project List">
              <ListItemText
                primary="Project List"
                sx={{
                  opacity: open ? 1 : 0,
                  color: location.pathname === "/main/view-project" ? globalStyle.blueColor : "",
                }}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontFamily: globalStyle.fontFamilyParagraph,
                  fontWeight: "bold",
                }}
              />
            </Tooltip>
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ backgroundColor: "#e6eaef" }}>
          <ListItemButton
            sx={{ justifyContent: open ? "initial" : "center" }}
            onClick={() => navigate("/main/meeting-list")}
          >
            <ListItemIcon sx={{ justifyContent: "center" }}>
              <StartIcon
                sx={{
                  color: location.pathname === "/main/meeting-list" ? globalStyle.blueColor : "",
                  transition: "color 0.3s ease",
                }}
              />
            </ListItemIcon>
            <Tooltip title="Meeting">
              <ListItemText
                primary="Meeting List"
                sx={{
                  opacity: open ? 1 : 0,
                  color: location.pathname === "/main/meeting-list" ? globalStyle.blueColor : "",
                }}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontFamily: globalStyle.fontFamilyParagraph,
                  fontWeight: "bold",
                }}
              />
            </Tooltip>
          </ListItemButton>
        </ListItem>

        {/* <ListItem disablePadding sx={{ backgroundColor: "#e6eaef" }}>
          <ListItemButton
            sx={{ justifyContent: open || mobileOpen ? "initial" : "center" }}
            onClick={() => handleNavigation("/main/add-ticket")}
          >
            <ListItemIcon sx={{ justifyContent: "center" }}>
              <AddCommentIcon
                sx={{
                  color: location.pathname === "/main/add-ticket" ? globalStyle.blueColor : "",
                  transition: "color 0.3s ease",
                }}
              />
            </ListItemIcon>
            <Tooltip title="Add Ticket">
              <ListItemText
                primary="Add Ticket"
                sx={{
                  opacity: open || mobileOpen ? 1 : 0,
                  color: location.pathname === "/main/add-ticket" ? globalStyle.blueColor : "",
                }}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontFamily: globalStyle.fontFamilyParagraph,
                  fontWeight: "bold",
                }}
              />
            </Tooltip>
          </ListItemButton>
        </ListItem> */}
        {/* {
          window.localStorage.getItem("role") === "admin" && (
            <ListItem disablePadding sx={{ backgroundColor: "#e6eaef" }}>
              <ListItemButton
                sx={{ justifyContent: open || mobileOpen ? "initial" : "center" }}
                onClick={() => handleNavigation("/main/view-ticket")}
              >
                <ListItemIcon sx={{ justifyContent: "center" }}>
                  <FactCheckIcon
                    sx={{
                      color: location.pathname === "/main/view-ticket" ? globalStyle.blueColor : "",
                      transition: "color 0.3s ease",
                    }}
                  />
                </ListItemIcon>
                <Tooltip title="View Ticket">
                  <ListItemText
                    primary="View Ticket"
                    sx={{
                      opacity: open || mobileOpen ? 1 : 0,
                      color: location.pathname === "/main/view-ticket" ? globalStyle.blueColor : "",
                    }}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontFamily: globalStyle.fontFamilyParagraph,
                      fontWeight: "bold",
                    }}
                  />
                </Tooltip>
              </ListItemButton>
            </ListItem>
          )
        } */}
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        sx={{ backgroundColor: globalStyle.bgColorWhite }}
      >
        <Toolbar>
          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2, color: "black" }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Button
            onClick={() => navigate("/main")}
            sx={{
              mb: 1,
              mt: 1,
              width: isMobile ? "auto" : 250,
              height: 57,
              borderRadius: 0,
              fontSize: isMobile ? "0.75rem" : "1rem",
              padding: isMobile ? "0 8px" : "0 16px",
            }}
          >
            {isMobile ? "eBeaconsofts Admin" : "eBeaconsofts Admin"}
          </Button>
          <Box flexGrow={1} />
          <Box display={"flex"} alignSelf={"center"} ml={1}>
            <IconButton
              edge="end"
              color="black"
              onClick={handleProfileMenuOpen}
            >
              <AccountCircleIcon fontSize="large" />
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{ mt: 1 }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer (temporary) */}
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: globalStyle.bgColorWhite,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        /* Desktop Drawer (permanent with hover) */
        <Drawer
          variant="permanent"
          open={open}
          onMouseOver={handleDrawerOpen}
          onMouseLeave={handleDrawerClose}
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: isMobile ? 2 : 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminHeader;